import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosConfig';
import Modal from 'react-modal';
import './AdminGeolocalisationPage.css';

Modal.setAppElement('#root');

function AdminGeolocalisationPage() {
  const [clients, setClients] = useState([]);
  const [historiques, setHistoriques] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [clientPhotos, setClientPhotos] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filterBase, setFilterBase] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [filterGps, setFilterGps] = useState('all'); // 'all', 'with', 'without'
  const [filterUser, setFilterUser] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [zones, setZones] = useState([]);
  const [users, setUsers] = useState([]);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const getBase = (zone) => {
    if (!zone) return '';
    const z = zone.toUpperCase();
    if (z.startsWith('DOUALA')) return 'DOUALA';
    if (z.startsWith('BAFOUSSAM') || z.startsWith('PERIPHERIQUE')) return 'BAFOUSSAM';
    if (z.startsWith('YAOUNDE') || z === 'HORS YAOUNDE') return 'YAOUNDE';
    return '';
  };

  useEffect(() => {
    applyFilters();
  }, [clients, historiques, filterBase, filterZone, filterGps, filterUser, filterDate]);

  const fetchData = async () => {
    try {
      // Récupérer tous les clients
      const clientsResponse = await axiosInstance.get('/admin/clients/all');
      // Les clients désactivés n'ont plus à être géolocalisés
      setClients(clientsResponse.data.filter(c => c.statut_validation !== 'inactif'));

      // Récupérer l'historique des géolocalisations
      const historiquesResponse = await axiosInstance.get('/admin/geolocalisations/historique');
      setHistoriques(historiquesResponse.data);

      // Récupérer les photos des clients qui ont des GPS
      const clientsWithGps = clientsResponse.data.filter(c => c.latitude && c.longitude);
      const photosMap = {};
      for (const client of clientsWithGps) {
        try {
          const photosResponse = await axiosInstance.get(`/clients/${client.id}/photos-pdv`);
          if (photosResponse.data && photosResponse.data.length > 0) {
            // Prendre la photo la plus récente
            const latestPhoto = photosResponse.data[0]; // Déjà trié par date décroissante
            photosMap[client.id] = latestPhoto.photo_url;
          }
        } catch (error) {
          console.error(`Erreur lors du chargement des photos pour client ${client.id}:`, error);
        }
      }
      setClientPhotos(photosMap);

      // Extraire les zones uniques
      const uniqueZones = [...new Set(clientsResponse.data.map(c => c.zone).filter(z => z))].sort();
      setZones(uniqueZones);


      // Extraire les utilisateurs uniques depuis l'historique
      const uniqueUsers = [...new Set(historiquesResponse.data.map(h => h.user?.nom).filter(u => u))].sort();
      setUsers(uniqueUsers);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...clients];

    // Filtrer par base
    if (filterBase) {
      filtered = filtered.filter(c => getBase(c.zone) === filterBase);
    }

    // Filtrer par zone
    if (filterZone) {
      filtered = filtered.filter(c => c.zone === filterZone);
    }

    // Filtrer par statut GPS
    if (filterGps === 'with') {
      filtered = filtered.filter(c => c.latitude && c.longitude);

      // Filtrer par utilisateur (qui a géolocalisé)
      if (filterUser !== 'all') {
        const clientIdsByUser = historiques
          .filter(h => h.user?.nom === filterUser)
          .map(h => h.client_id);
        filtered = filtered.filter(c => clientIdsByUser.includes(c.id));
      }

      // Filtrer par date de géolocalisation
      if (filterDate) {
        const clientIdsByDate = historiques
          .filter(h => {
            const geoDate = new Date(h.date_geolocalisation).toISOString().split('T')[0];
            return geoDate === filterDate;
          })
          .map(h => h.client_id);
        filtered = filtered.filter(c => clientIdsByDate.includes(c.id));
      }
    } else if (filterGps === 'without') {
      filtered = filtered.filter(c => !c.latitude || !c.longitude);
    }

    setFilteredClients(filtered);
  };

  const handleDeleteGps = async (clientId, clientName) => {
    if (!window.confirm(`Supprimer les coordonnées GPS de "${clientName}" ?`)) {
      return;
    }

    try {
      await axiosInstance.put(`/admin/clients/${clientId}`, {
        latitude: null,
        longitude: null
      });
      alert('Coordonnées GPS supprimées avec succès!');
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression des coordonnées GPS');
    }
  };

  // Fonction pour récupérer les infos de géolocalisation d'un client
  const getGeolocalisationInfo = (clientId) => {
    const histo = historiques.find(h => h.client_id === clientId);
    if (!histo) return null;
    return {
      date: new Date(histo.date_geolocalisation),
      user: histo.user?.nom || '-'
    };
  };

  // Fonction pour ouvrir le modal avec la photo
  const openPhotoModal = (clientId, clientName) => {
    const photoUrl = clientPhotos[clientId];
    if (photoUrl) {
      // Construire l'URL complète si c'est un chemin relatif
      const fullUrl = photoUrl.startsWith('http') ? photoUrl : `https://spmerchflow.com:8000${photoUrl}`;
      setSelectedPhoto(fullUrl);
      setSelectedClientName(clientName);
      setPhotoModalOpen(true);
    }
  };

  // Fonction pour fermer le modal
  const closePhotoModal = () => {
    setPhotoModalOpen(false);
    setSelectedPhoto(null);
    setSelectedClientName('');
  };

  if (isLoading) {
    return <div className="admin-page">Chargement...</div>;
  }

  // Calculer les statistiques (basées sur la base/zone sélectionnée ou globales)
  const clientsForStats = clients
    .filter(c => !filterBase || getBase(c.zone) === filterBase)
    .filter(c => !filterZone || c.zone === filterZone);
  const totalClients = clientsForStats.length;
  const clientsWithGps = clientsForStats.filter(c => c.latitude && c.longitude).length;
  const clientsWithoutGps = totalClients - clientsWithGps;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>📍 Géolocalisation des Points de Vente</h1>
      </div>

      {/* Statistiques globales ou par zone */}
      <div className="stats-section">
        <h3 className="stats-title">
          {filterZone ? `Zone: ${filterZone}` : filterBase ? `Base: ${filterBase}` : 'Toutes les bases'}
        </h3>
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-value">{totalClients}</div>
            <div className="stat-label">Total PDV</div>
          </div>
          <div className="stat-card success">
            <div className="stat-value">{clientsWithGps}</div>
            <div className="stat-label">Géolocalisés</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-value">{clientsWithoutGps}</div>
            <div className="stat-label">À géolocaliser</div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="filters-container">
        <div className="filter-group">
          <label>Filtrer par base:</label>
          <select value={filterBase} onChange={(e) => {
            setFilterBase(e.target.value);
            setFilterZone('');
          }}>
            <option value="">Toutes les bases</option>
            <option value="YAOUNDE">YAOUNDE</option>
            <option value="DOUALA">DOUALA</option>
            <option value="BAFOUSSAM">BAFOUSSAM</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Filtrer par zone:</label>
          <select value={filterZone} onChange={(e) => setFilterZone(e.target.value)}>
            <option value="">Toutes les zones</option>
            {zones
              .filter(z => !filterBase || getBase(z) === filterBase)
              .map(zone => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Filtrer par statut GPS:</label>
          <select value={filterGps} onChange={(e) => {
            setFilterGps(e.target.value);
            // Réinitialiser les filtres utilisateur et date quand on change de filtre GPS
            if (e.target.value !== 'with') {
              setFilterUser('all');
              setFilterDate('');
            }
          }}>
            <option value="all">Tous les PDV</option>
            <option value="with">Avec GPS 📍</option>
            <option value="without">Sans GPS ❓</option>
          </select>
        </div>

        {filterGps === 'with' && (
          <>
            <div className="filter-group">
              <label>Filtrer par utilisateur:</label>
              <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)}>
                <option value="all">Tous les utilisateurs</option>
                {users.map(user => (
                  <option key={user} value={user}>{user}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Filtrer par date:</label>
              <select value={filterDate} onChange={(e) => setFilterDate(e.target.value)}>
                <option value="">Toutes les dates</option>
                {[...new Set(historiques.map(h =>
                  new Date(h.date_geolocalisation).toISOString().split('T')[0]
                ))].sort().reverse().map(date => (
                  <option key={date} value={date}>{new Date(date).toLocaleDateString('fr-FR')}</option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {/* Tableau des clients */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>PDV</th>
              <th>Zone</th>
              <th>Commercial</th>
              <th>Localisation</th>
              <th>GPS</th>
              <th>Photo PDV</th>
              {filterGps === 'with' && <th>Date</th>}
              {filterGps === 'with' && <th>Utilisateur</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map(client => {
              const geoInfo = getGeolocalisationInfo(client.id);
              const hasPhoto = clientPhotos[client.id];
              return (
                <tr key={client.id}>
                  <td>
                    <strong>{client.nom_client}</strong>
                    {client.contact && <div style={{fontSize: '0.85rem', color: '#666'}}>📞 {client.contact}</div>}
                  </td>
                  <td>{client.zone || '-'}</td>
                  <td>{client.commercial?.nom || '-'}</td>
                  <td>{client.localisation || '-'}</td>
                  <td>
                    {client.latitude && client.longitude ? (
                      <div className="coords-cell present">
                        <div>Lat: <span className="coord-value">{client.latitude.toFixed(6)}</span></div>
                        <div>Lng: <span className="coord-value">{client.longitude.toFixed(6)}</span></div>
                      </div>
                    ) : (
                      <span className="no-gps">Non géolocalisé</span>
                    )}
                  </td>
                  <td>
                    {hasPhoto ? (
                      <button
                        onClick={() => openPhotoModal(client.id, client.nom_client)}
                        className="photo-link"
                      >
                        📷 Voir la photo
                      </button>
                    ) : (
                      <span className="no-photo">-</span>
                    )}
                  </td>
                  {filterGps === 'with' && (
                    <td>
                      {geoInfo ? (
                        <div className="date-cell">
                          <div className="date-day">{geoInfo.date.toLocaleDateString('fr-FR')}</div>
                          <div className="date-time">{geoInfo.date.toLocaleTimeString('fr-FR')}</div>
                        </div>
                      ) : '-'}
                    </td>
                  )}
                  {filterGps === 'with' && (
                    <td>{geoInfo?.user || '-'}</td>
                  )}
                  <td>
                    {client.latitude && client.longitude ? (
                      <>
                        <button
                          onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${client.latitude},${client.longitude}`, '_blank')}
                          className="action-button view-button"
                          title="Voir sur carte"
                        >
                          🗺️
                        </button>
                        <button
                          onClick={() => handleDeleteGps(client.id, client.nom_client)}
                          className="action-button delete-button"
                          title="Supprimer GPS"
                        >
                          ❌
                        </button>
                      </>
                    ) : (
                      <span className="no-action">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredClients.length === 0 && (
          <div className="no-data">
            Aucun client trouvé avec les filtres actuels
          </div>
        )}
      </div>

      {/* Modal pour afficher la photo en grand */}
      <Modal
        isOpen={photoModalOpen}
        onRequestClose={closePhotoModal}
        className="photo-modal"
        overlayClassName="photo-modal-overlay"
      >
        <div className="photo-modal-content">
          <div className="photo-modal-header">
            <h2>Photo du PDV: {selectedClientName}</h2>
            <button onClick={closePhotoModal} className="close-modal-btn">✕</button>
          </div>
          <div className="photo-modal-body">
            {selectedPhoto && (
              <img src={selectedPhoto} alt={`Photo du PDV ${selectedClientName}`} className="photo-modal-image" />
            )}
          </div>
          <div className="photo-modal-footer">
            <button onClick={closePhotoModal} className="button-secondary">Fermer</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AdminGeolocalisationPage;
